/**
 * Auth Guard (Protected Route)
 *
 * Protects routes that require authentication.
 * Redirects to sign-in page if user is not authenticated.
 *
 * Usage:
 * ```tsx
 * <AuthGuard>
 *   <DashboardPage />
 * ./auth-guard>
 *
 * // With custom redirect
 * <AuthGuard redirectTo="/login">
 *   <SettingsPage />
 * ./auth-guard>
 * ```
 */

import { useEffect } from "react"

import { useAuth } from "@/contexts/auth-context"

import { LoadingFallback } from "./loading-fallback"

import type { AuthGuardProps } from "./types"

export function AuthGuard({
  children,
  fallback,
}: AuthGuardProps) {
  const { isAuthenticated, isLoading, login } = useAuth()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      login()
    }
  }, [isLoading, isAuthenticated, login])

  if (isLoading || !isAuthenticated) {
    return fallback ? <>{fallback}</> : <LoadingFallback />
  }

  return <>{children}</>
}
